import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Loader2 } from 'lucide-react';
import { ServiceLabel } from '../contexts/StreamingServicesContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShowReviewsModal = ({ show, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [reviews, setReviews] = useState([]);

  // Handle dialog close - only close if explicitly requested
  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
    }
  };

  useEffect(() => {
    loadShowData();
  }, []);

  const loadShowData = async () => {
    setLoading(true);
    try {
      // Get statistics - use tmdb_id for accurate matching when available
      const statsParams = show.tmdb_id ? `?tmdb_id=${show.tmdb_id}` : '';
      const statsResponse = await axios.get(
        `${API}/shows/${encodeURIComponent(show.show_title || show.title)}/statistics${statsParams}`,
        { withCredentials: true }
      );
      setStatistics(statsResponse.data);

      // Get all reviews from friends for this show - use tmdb_id when available
      const reviewsParams = show.tmdb_id ? `?tmdb_id=${show.tmdb_id}` : '';
      const reviewsResponse = await axios.get(
        `${API}/reviews/show/${encodeURIComponent(show.show_title || show.title)}${reviewsParams}`,
        { withCredentials: true }
      );
      setReviews(reviewsResponse.data);
    } catch (error) {
      console.error('Error loading show data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent className="show-reviews-modal" data-testid="show-reviews-modal" aria-describedby={undefined}>
        <DialogHeader className="modal-header">
          <DialogTitle>
            {show.show_title}
            {(show.streaming_service || show.custom_service) && (
              <div className="streaming-badge">
                <ServiceLabel name={show.custom_service || show.streaming_service} />
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <Loader2 className="spinner" />
              <p>Loading reviews...</p>
            </div>
          ) : (
            <>
              {(show.backdrop_url || show.show_image_url) && (
                <div className="show-poster-section">
                  <img 
                    src={show.backdrop_url || show.show_image_url} 
                    alt={show.show_title} 
                    className={show.backdrop_url ? "show-backdrop" : "show-poster"} 
                  />
                </div>
              )}

              {/* Show Info Section */}
              {(show.release_date || (show.genres && show.genres.length > 0)) && (
                <div className="show-info-section">
                  {show.release_date && (
                    <span className="info-badge">{show.release_date.substring(0, 4)}</span>
                  )}
                  {show.genres && show.genres.slice(0, 3).map((genre, index) => (
                    <span key={index} className="info-badge genre">{genre}</span>
                  ))}
                </div>
              )}

              {/* Synopsis Section */}
              {show.overview && (
                <div className="show-synopsis-section">
                  <p className="show-synopsis">{show.overview}</p>
                </div>
              )}

              {/* Episode Status Section */}
              {show.show_status && (
                <div className="episode-status-section">
                  <h3>📺 Show Status</h3>
                  <div className="episode-status-content">
                    <div className="status-main">
                      <span className={`status-badge status-${show.show_status?.toLowerCase().replace(/\s+/g, '-')}`}>
                        {show.show_status}
                      </span>
                    </div>
                    
                    {show.next_episode && show.next_episode.air_date && (
                      <div className="episode-detail">
                        <div className="episode-label">Next Episode</div>
                        <div className="episode-info">
                          <span className="episode-number">
                            S{String(show.next_episode.season_number).padStart(2, '0')}E{String(show.next_episode.episode_number).padStart(2, '0')}
                          </span>
                          {show.next_episode.name && (
                            <span className="episode-name">"{show.next_episode.name}"</span>
                          )}
                        </div>
                        {(() => {
                          // Get user's timezone and friendly location name
                          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                          
                          // Extract location from timezone (e.g., "Pacific/Auckland" -> "Auckland")
                          const locationParts = userTimezone.split('/');
                          let location = locationParts[locationParts.length - 1].replace(/_/g, ' ');
                          
                          // Map common timezones to friendlier names and flags
                          const locationData = {
                            'Auckland': { name: 'NZ', flag: '🇳🇿' },
                            'Wellington': { name: 'NZ', flag: '🇳🇿' },
                            'Sydney': { name: 'Australia', flag: '🇦🇺' },
                            'Melbourne': { name: 'Australia', flag: '🇦🇺' },
                            'Brisbane': { name: 'Australia', flag: '🇦🇺' },
                            'Perth': { name: 'Australia', flag: '🇦🇺' },
                            'London': { name: 'UK', flag: '🇬🇧' },
                            'Dublin': { name: 'Ireland', flag: '🇮🇪' },
                            'New York': { name: 'US East Coast', flag: '🇺🇸' },
                            'Los Angeles': { name: 'US West Coast', flag: '🇺🇸' },
                            'Chicago': { name: 'US Central', flag: '🇺🇸' },
                            'Denver': { name: 'US Mountain', flag: '🇺🇸' },
                            'Phoenix': { name: 'US Arizona', flag: '🇺🇸' },
                            'Toronto': { name: 'Canada', flag: '🇨🇦' },
                            'Vancouver': { name: 'Canada', flag: '🇨🇦' },
                            'Montreal': { name: 'Canada', flag: '🇨🇦' },
                            'Paris': { name: 'France', flag: '🇫🇷' },
                            'Berlin': { name: 'Germany', flag: '🇩🇪' },
                            'Amsterdam': { name: 'Netherlands', flag: '🇳🇱' },
                            'Stockholm': { name: 'Sweden', flag: '🇸🇪' },
                            'Tokyo': { name: 'Japan', flag: '🇯🇵' },
                            'Singapore': { name: 'Singapore', flag: '🇸🇬' },
                            'Hong Kong': { name: 'Hong Kong', flag: '🇭🇰' },
                            'Mumbai': { name: 'India', flag: '🇮🇳' },
                            'Kolkata': { name: 'India', flag: '🇮🇳' },
                            'Delhi': { name: 'India', flag: '🇮🇳' },
                            'Dubai': { name: 'UAE', flag: '🇦🇪' },
                            'Johannesburg': { name: 'South Africa', flag: '🇿🇦' },
                            'Sao Paulo': { name: 'Brazil', flag: '🇧🇷' },
                            'Mexico City': { name: 'Mexico', flag: '🇲🇽' },
                            'Seoul': { name: 'South Korea', flag: '🇰🇷' },
                            'Manila': { name: 'Philippines', flag: '🇵🇭' },
                            'Jakarta': { name: 'Indonesia', flag: '🇮🇩' },
                            'Bangkok': { name: 'Thailand', flag: '🇹🇭' }
                          };
                          const locData = locationData[location] || { name: location, flag: '🌍' };
                          const locationFlag = locData.flag;
                          
                          // Calculate local release time (end of US broadcast day = midnight PT)
                          const airDateStr = show.next_episode.air_date;
                          // Use midnight PT (start of next day after broadcast date)
                          const releaseTimePT = new Date(`${airDateStr}T00:00:00-08:00`);
                          releaseTimePT.setDate(releaseTimePT.getDate() + 1); // Add one day to get midnight at END of broadcast day
                          
                          // Format local date and time
                          const localDate = releaseTimePT.toLocaleDateString('en-NZ', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          });
                          const localTime = releaseTimePT.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit'
                          });
                          
                          // Calculate countdown based on local release time using calendar days
                          const now = new Date();
                          const diffMs = releaseTimePT - now;
                          
                          // Compare calendar days (not just hours)
                          const releaseDay = new Date(releaseTimePT);
                          releaseDay.setHours(0, 0, 0, 0);
                          const todayStart = new Date();
                          todayStart.setHours(0, 0, 0, 0);
                          const dayDiff = Math.round((releaseDay - todayStart) / (1000 * 60 * 60 * 24));
                          
                          let countdownBadge = null;
                          if (diffMs <= 0) {
                            countdownBadge = <span className="countdown available">Available now</span>;
                          } else if (dayDiff === 0) {
                            countdownBadge = <span className="countdown today">Today!</span>;
                          } else if (dayDiff === 1) {
                            countdownBadge = <span className="countdown soon">Tomorrow</span>;
                          } else if (dayDiff <= 7) {
                            countdownBadge = <span className="countdown soon">In {dayDiff} days</span>;
                          }
                          
                          // US broadcast date for reference
                          const usBroadcastDate = new Date(airDateStr + 'T12:00:00').toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          });
                          
                          return (
                            <>
                              <div className="episode-air-date">
                                <span className="local-release">
                                  {locationFlag} {localDate}
                                </span>
                                {countdownBadge}
                              </div>
                              <div className="timezone-note">
                                <span className="timezone-detail">🇺🇸 US broadcast: {usBroadcastDate} • Released at midnight PT</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    
                    {show.last_episode && show.last_episode.air_date && !show.next_episode && (
                      <div className="episode-detail">
                        <div className="episode-label">Last Episode</div>
                        <div className="episode-info">
                          <span className="episode-number">
                            S{String(show.last_episode.season_number).padStart(2, '0')}E{String(show.last_episode.episode_number).padStart(2, '0')}
                          </span>
                          {show.last_episode.name && (
                            <span className="episode-name">"{show.last_episode.name}"</span>
                          )}
                        </div>
                        <div className="episode-air-date muted">
                          Aired {new Date(show.last_episode.air_date).toLocaleDateString('en-NZ', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                      </div>
                    )}

                    {show.show_status === 'Ended' && (
                      <div className="status-message">
                        This show has ended.
                      </div>
                    )}

                    {show.show_status === 'Canceled' && (
                      <div className="status-message warning">
                        This show was canceled.
                      </div>
                    )}

                    {show.show_status === 'Returning Series' && !show.next_episode && show.in_production && (
                      <div className="status-message">
                        A new season is in production. Air dates have not been announced yet.
                      </div>
                    )}

                    {show.show_status === 'Returning Series' && !show.next_episode && !show.in_production && (
                      <div className="status-message">
                        This show is awaiting renewal. No new episodes have been announced.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {statistics && (
                <div className="statistics-section">
                  <h3>Statistics</h3>
                  <div className="stats-grid">
                    {statistics.friends.average ? (
                      <div className="stat-card">
                        <div className="stat-label">👥 Friends' Average</div>
                        <div className="stat-value">⭐ {statistics.friends.average}/10</div>
                        <div className="stat-count">{statistics.friends.count} review{statistics.friends.count !== 1 ? 's' : ''}</div>
                      </div>
                    ) : (
                      <div className="stat-card empty">
                        <div className="stat-label">👥 Friends' Average</div>
                        <div className="stat-empty">No reviews yet</div>
                      </div>
                    )}

                    {statistics.all_users.average ? (
                      <div className="stat-card">
                        <div className="stat-label">🌎 All Users' Average</div>
                        <div className="stat-value">⭐ {statistics.all_users.average}/10</div>
                        <div className="stat-count">{statistics.all_users.count} review{statistics.all_users.count !== 1 ? 's' : ''}</div>
                      </div>
                    ) : (
                      <div className="stat-card empty">
                        <div className="stat-label">🌎 All Users' Average</div>
                        <div className="stat-empty">No reviews yet</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="reviews-section">
                <h3>Friend Reviews</h3>
                {reviews.length === 0 ? (
                  <div className="no-reviews">
                    <p>No reviews from friends yet. Be the first to review!</p>
                  </div>
                ) : (
                  <div className="reviews-list">
                    {reviews.map((review) => (
                      <div key={review.id} className="review-card" data-testid={`review-${review.id}`}>
                        <div className="review-header">
                          <Avatar className="review-avatar">
                            <AvatarImage src={review.user.picture} alt={review.user.name} />
                            <AvatarFallback>{review.user.name[0]}</AvatarFallback>
                          </Avatar>
                          <div className="review-user-info">
                            <div className="review-user-name">{review.user.name}</div>
                            <div className="review-date">{formatDate(review.created_at)}</div>
                          </div>
                          <div className="review-rating">⭐ {review.rating}/10</div>
                        </div>
                        <div className="review-text">{review.review_text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <style jsx>{`
          .show-reviews-modal {
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            background: #1a1a1a !important;
            color: #ffffff !important;
            border: 1px solid #333333 !important;
            max-width: 700px;
          }

          .show-reviews-modal :global(button[class*="absolute"]) {
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
          }

          .show-reviews-modal :global(button[class*="absolute"]:hover) {
            color: #ffffff !important;
            background: transparent !important;
          }

          .show-reviews-modal :global(button[class*="absolute"]:focus) {
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }

          .modal-header {
            flex-shrink: 0;
            padding-bottom: 1rem;
            border-bottom: 1px solid #333333;
          }

          .modal-header :global(h2) {
            color: #ffffff !important;
            font-size: 1.5rem;
          }

          .streaming-badge {
            display: inline-block;
            margin-left: 0.75rem;
            padding: 0.3rem 0.75rem;
            background: #E62F88;
            color: #000000;
            border-radius: 8px;
            font-size: 0.85rem;
            font-weight: 600;
          }

          .modal-content {
            flex: 1;
            overflow-y: auto;
            padding: 1.5rem 0;
            -webkit-overflow-scrolling: touch;
          }

          .loading-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem;
            color: #b0b0b0;
          }

          .loading-state :global(.spinner) {
            width: 40px;
            height: 40px;
            color: #E62F88;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .loading-state p {
            margin-top: 1rem;
          }

          .show-poster-section {
            display: flex;
            justify-content: center;
            margin-bottom: 1.5rem;
          }

          .show-poster {
            width: 150px;
            height: 225px;
            object-fit: cover;
            border-radius: 8px;
            border: 2px solid #333333;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
          }

          .show-backdrop {
            width: 100%;
            max-width: 500px;
            height: auto;
            aspect-ratio: 16/9;
            object-fit: cover;
            border-radius: 8px;
            border: 2px solid #333333;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
          }

          .show-info-section {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            justify-content: center;
            margin-bottom: 1.5rem;
          }

          .info-badge {
            padding: 0.25rem 0.75rem;
            background: #333333;
            color: #ffffff;
            border-radius: 20px;
            font-size: 0.8rem;
          }

          .info-badge.genre {
            background: #2a2a2a;
            border: 1px solid #444444;
          }

          .show-synopsis-section {
            margin-bottom: 1.5rem;
            padding: 1rem;
            background: #1a1a1a;
            border-radius: 8px;
            border: 1px solid #333333;
          }

          .show-synopsis {
            font-size: 0.9rem;
            color: #b0b0b0;
            line-height: 1.6;
            margin: 0;
            text-align: left;
          }

          .episode-status-section {
            background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
            border: 1px solid #E62F88;
            border-radius: 12px;
            padding: 1.25rem;
            margin-bottom: 1.5rem;
          }

          .episode-status-section h3 {
            font-size: 1.1rem;
            font-weight: 600;
            color: #E62F88;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid rgba(230, 47, 136, 0.3);
          }

          .episode-status-content {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .status-main {
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .status-badge {
            display: inline-block;
            padding: 0.4rem 1rem;
            border-radius: 20px;
            font-size: 0.85rem;
            font-weight: 600;
          }

          .status-returning-series {
            background: #22c55e;
            color: #000000;
          }

          .status-ended {
            background: #6b7280;
            color: #ffffff;
          }

          .status-canceled {
            background: #ef4444;
            color: #ffffff;
          }

          .status-in-production {
            background: #f59e0b;
            color: #000000;
          }

          .episode-detail {
            background: rgba(255, 255, 255, 0.05);
            padding: 1rem;
            border-radius: 8px;
          }

          .episode-label {
            font-size: 0.75rem;
            color: #888888;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 0.5rem;
          }

          .episode-info {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
          }

          .episode-number {
            font-size: 1.1rem;
            font-weight: 700;
            color: #E62F88;
          }

          .episode-name {
            font-size: 0.95rem;
            color: #ffffff;
          }

          .episode-air-date {
            font-size: 0.9rem;
            color: #b0b0b0;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
          }

          .episode-air-date.muted {
            color: #888888;
          }

          .countdown {
            display: inline-block;
            padding: 0.2rem 0.6rem;
            border-radius: 4px;
            font-size: 0.8rem;
            font-weight: 600;
          }

          .countdown.today {
            background: #22c55e;
            color: #000000;
          }

          .countdown.soon {
            background: #3b82f6;
            color: #ffffff;
          }

          .countdown.available {
            background: #22c55e;
            color: #000000;
          }

          .status-message {
            font-size: 0.9rem;
            color: #b0b0b0;
            padding: 0.75rem;
            background: rgba(230, 47, 136, 0.1);
            border-radius: 8px;
            line-height: 1.5;
          }

          .timezone-note {
            font-size: 0.75rem;
            margin-top: 0.5rem;
            padding: 0.5rem 0.75rem;
            background: rgba(255, 255, 255, 0.03);
            border-radius: 4px;
          }

          .timezone-detail {
            color: #666666;
            font-style: italic;
          }

          .local-release {
            font-weight: 500;
          }

          .status-message.warning {
            background: rgba(239, 68, 68, 0.15);
          }
        .capacity-info {
          background: #2a2a2a;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }

        .capacity-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
        }

        .capacity-row:not(:last-child) {
          border-bottom: 1px solid #333333;
        }

        .capacity-label {
          color: #888888;
          font-size: 0.9rem;
        }

        .capacity-value {
          font-size: 1rem;
          font-weight: 600;
          color: #ffffff;
        }

        .capacity-value.good {
          color: #4caf50;
        }

        .capacity-value.warning {
          color: #f59e0b;
        }

        .index-results {
          display: flex;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #2a2a2a;
          border-radius: 8px;
        }

        .result-item {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .result-label {
          font-size: 0.75rem;
          color: #888888;
          margin-bottom: 0.25rem;
        }

        .result-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
        }

        .result-value.good {
          color: #4caf50;
        }

        .result-value.warning {
          color: #f59e0b;
        }

          .statistics-section {
            margin-bottom: 2rem;
          }

          .statistics-section h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #E62F88;
            margin-bottom: 1rem;
          }

          .stats-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }

          .stat-card {
            background: #2a2a2a;
            border: 1px solid #333333;
            border-radius: 8px;
            padding: 1rem;
            text-align: center;
          }

          .stat-card.empty {
            opacity: 0.6;
          }

          .stat-label {
            font-size: 0.9rem;
            color: #b0b0b0;
            margin-bottom: 0.5rem;
          }

          .stat-value {
            font-size: 1.5rem;
            font-weight: 700;
            color: #E62F88;
            margin-bottom: 0.25rem;
          }

          .stat-count {
            font-size: 0.85rem;
            color: #b0b0b0;
          }

          .stat-empty {
            font-size: 0.9rem;
            color: #666666;
            padding: 0.5rem 0;
          }

          .reviews-section h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #E62F88;
            margin-bottom: 1rem;
          }

          .no-reviews {
            text-align: center;
            padding: 2rem;
            color: #b0b0b0;
            background: #2a2a2a;
            border: 1px solid #333333;
            border-radius: 8px;
          }

          .reviews-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .review-card {
            background: #2a2a2a;
            border: 1px solid #333333;
            border-radius: 8px;
            padding: 1.25rem;
          }

          .review-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1rem;
          }

          .review-avatar {
            width: 40px;
            height: 40px;
            flex-shrink: 0;
          }

          .review-user-info {
            flex: 1;
          }

          .review-user-name {
            font-weight: 600;
            color: #ffffff;
            font-size: 1rem;
          }

          .review-date {
            font-size: 0.85rem;
            color: #b0b0b0;
          }

          .review-rating {
            background: #E62F88;
            color: #000000;
            padding: 0.35rem 0.75rem;
            border-radius: 20px;
            font-weight: 600;
            font-size: 0.9rem;
            flex-shrink: 0;
          }

          .review-text {
            color: #b0b0b0;
            line-height: 1.6;
            font-size: 0.95rem;
          }

          @media (max-width: 768px) {
            .show-reviews-modal {
              max-width: 100vw;
              max-height: 100vh;
              width: 100vw;
              height: 100vh;
              margin: 0;
              border-radius: 0;
              position: fixed;
              top: 0;
              left: 0;
              transform: none !important;
            }

            .modal-header {
              padding: 1rem 1rem 0.75rem;
            }

            .modal-content {
              padding: 1rem;
            }

            .show-poster {
              width: 120px;
              height: 180px;
            }

            .stats-grid {
              grid-template-columns: 1fr;
            }

            .review-header {
              flex-wrap: wrap;
            }

            .review-rating {
              margin-left: auto;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default ShowReviewsModal;
